import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Wallet, Megaphone, FileText, Feather, Crown, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageHeader, Table } from "@/admin/ui";
import { Button } from "@/components/ui/button";

const COLORS = { ads: "#00E5FF", pr: "#7C3AED", author: "#22c55e", premium: "#F59E0B" };
const fmt = (n: number) => `$${(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function Revenue() {
  const { data } = useQuery({
    queryKey: ["admin-revenue"],
    queryFn: async () => (await api.get("/admin/revenue")).data,
  });
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [exporting, setExporting] = useState<string | null>(null);

  const download = async (path: string, filename: string) => {
    setExporting(path);
    try {
      const params = new URLSearchParams();
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      const res = await api.get(`${path}?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch {
      toast.error("Export failed — try again");
    } finally {
      setExporting(null);
    }
  };

  const months = data?.months || [];
  const totals = data?.totals || {};
  const thisMonth = new Date().toISOString().slice(0, 7);
  const lastMonthDate = new Date();
  lastMonthDate.setMonth(lastMonthDate.getMonth() - 1);
  const lastMonth = lastMonthDate.toISOString().slice(0, 7);
  const cur = months.find((m: any) => m.month === thisMonth);
  const prev = months.find((m: any) => m.month === lastMonth);

  const cards = [
    { label: "This month", value: fmt(cur?.total || 0), sub: `${cur?.count || 0} payments` },
    { label: "Last month", value: fmt(prev?.total || 0), sub: `${prev?.count || 0} payments` },
    { label: "All time", value: fmt(totals.total || 0), sub: `${totals.count || 0} payments` },
  ];
  const streams = [
    { key: "ads", label: "Ads", icon: Megaphone },
    { key: "pr", label: "PR", icon: FileText },
    { key: "author", label: "Authors", icon: Feather },
    { key: "premium", label: "Premium", icon: Crown },
  ];

  return (
    <div data-testid="admin-revenue-page">
      <PageHeader title="Revenue" sub="Monthly earnings across ads, PR publications, author subscriptions and premium readers" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} data-testid={`revenue-card-${c.label.toLowerCase().replace(" ", "-")}`}
            className="bg-[#121212] border border-white/10 rounded-xl p-5">
            <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
              <Wallet className="h-3.5 w-3.5 text-[#00E5FF]" /> {c.label}
            </p>
            <p className="font-mono text-3xl font-bold text-white mt-2">{c.value}</p>
            <p className="font-mono text-[10px] text-gray-500 mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {streams.map((s) => (
          <div key={s.key} className="bg-[#121212] border border-white/10 rounded-lg px-4 py-3">
            <p className="font-mono text-[9px] uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
              <s.icon className="h-3 w-3" style={{ color: COLORS[s.key as keyof typeof COLORS] }} /> {s.label} (all time)
            </p>
            <p className="font-mono text-lg font-bold text-white mt-1">{fmt(totals[s.key] || 0)}</p>
          </div>
        ))}
      </div>

      <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mb-6" data-testid="accounting-reports">
        <h3 className="font-heading font-bold text-white text-sm mb-1">Accounting reports</h3>
        <p className="text-gray-500 text-xs mb-4">
          CSV exports for your accounting team — leave the dates blank for an all-time report.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-gray-500 block mb-1">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-date-from"
              className="bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-1.5 text-sm text-white font-mono [color-scheme:dark]" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-gray-500 block mb-1">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-date-to"
              className="bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-1.5 text-sm text-white font-mono [color-scheme:dark]" />
          </div>
          <Button size="sm" data-testid="export-transactions-csv"
            disabled={exporting === "/admin/reports/transactions.csv"}
            onClick={() => download("/admin/reports/transactions.csv", "newslayer_transactions.csv")}
            className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Transactions CSV
          </Button>
          <Button size="sm" variant="outline" data-testid="export-ad-orders-csv"
            disabled={exporting === "/admin/reports/ad-orders.csv"}
            onClick={() => download("/admin/reports/ad-orders.csv", "newslayer_ad_orders.csv")}
            className="border-white/15 text-gray-300 hover:bg-white/5">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Ad orders CSV
          </Button>
        </div>
      </div>

      {months.length > 0 && (
        <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mb-6">
          <h3 className="font-heading font-bold text-white text-sm mb-4">Monthly breakdown</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={months} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
                <XAxis dataKey="month" tick={{ fill: "#6b7280", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#6b7280", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 8, fontSize: 11, fontFamily: "JetBrains Mono", color: "#fff" }}
                  formatter={(v: any, name: any) => [fmt(v), name]} />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                <Bar dataKey="ads" stackId="rev" fill={COLORS.ads} name="Ads" />
                <Bar dataKey="pr" stackId="rev" fill={COLORS.pr} name="PR" />
                <Bar dataKey="author" stackId="rev" fill={COLORS.author} name="Authors" />
                <Bar dataKey="premium" stackId="rev" fill={COLORS.premium} name="Premium" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <Table headers={["Month", "Ads", "PR", "Authors", "Premium", "Payments", "Total"]}>
        {[...months].reverse().map((m: any) => (
          <tr key={m.month} data-testid={`revenue-row-${m.month}`}>
            <td className="px-4 py-3 font-mono text-sm text-white">{m.month}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">{fmt(m.ads)}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">{fmt(m.pr)}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">{fmt(m.author)}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">{fmt(m.premium)}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-500">{m.count}</td>
            <td className="px-4 py-3 font-mono text-sm font-bold text-[#00E5FF]">{fmt(m.total)}</td>
          </tr>
        ))}
      </Table>
      {months.length === 0 && (
        <p className="text-gray-500 text-sm mt-6 text-center py-8 border border-dashed border-white/10 rounded-lg">
          No paid transactions yet — revenue appears here as soon as the first payment lands.
        </p>
      )}
    </div>
  );
}
